import React, { useEffect, useState } from 'react';
import SbButton from '@Components/Button';
import Title from '@Components/Title';
import { Grid, Switch, Typography } from '@mui/material';
import AccountDetails from './AccountDetails';
import Charges from './Charges';
import ExtraCharges, { ExtraChargeType } from './ExtraCharge';
import DateWiseGlobalCharges from '@Components/DateWiseGlobalCharges';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import useScroll from '../../useScroll';
import { shipmentLockRate, updateExtraCharge } from '@Reducers/AdminShipment';
import { isActionAllowed } from '../../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { ShipmentControl } from '@Services/shipmentActionControl';
import { Carrier, CarrierResponse } from './constants';
import API from '@Services/apiAxios';
import { SuccessErrorModalOpen, SuccessErrorModalReset } from '@Reducers/SuccessErrorModal';

export default function ShipmentCharges() {
  const dispatch = useDispatch<AppDispatch>();
  const { shipmentChargesRef } = useScroll();
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const { organizationData } = useSelector((state: RootStateType) => state.orgSetup);
  const [showPurchasePricing, setShowPurchasePricing] = useState(false);

  useEffect(() => {
    const fetchCarriers = async () => {
      try {
        const response = await API.get<CarrierResponse>('/organization/carrier/group-by-carrier');
        setCarriers(response.data.data);
      } catch (error) {
        console.error('Error fetching carriers:', error);
      }
    };

    fetchCarriers();
  }, []);

  useEffect(() => {
    const fetchCarriers = async () => {
      try {
        const response = await API.get<CarrierResponse>('/organization/carrier/group-by-carrier');
        setCarriers(response.data.data);
      } catch (error) {
        console.error('Error fetching carriers:', error);
      }
    };

    fetchCarriers();
  }, []);

  const isLockRatesWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.lock_rates,
    PERFORM_ACTION.write,
  );

  const isGlobalChargesWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.global_charges,
    PERFORM_ACTION.write,
  );
  const isSellingChargesReadAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.selling_charges,
    PERFORM_ACTION.read,
  );
  const isBuyingChargesReadAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.buying_charges,
    PERFORM_ACTION.read,
  );
  const isSellingChargesWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.selling_charges,
    PERFORM_ACTION.write,
  );
  const isBuyingChargesWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.buying_charges,
    PERFORM_ACTION.write,
  );

  const shipmentActionControl = new ShipmentControl(shipmentById);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  if (!shipmentById) return <></>;
  const { sellingPricing, buyingPricing, shipmentNumber, extraCharges, purchasePricing, isInvoiceGenerated } =
    shipmentById || {};

  const isEmptyObject = (obj: unknown): boolean => {
    if (!obj || typeof obj !== 'object') return true;
    return Object.keys(obj as Record<string, any>).length === 0;
  };

  const handleLockRate = () => {
    const addCharges = extraCharges?.selling?.add;
    const discountCharges = extraCharges?.selling?.discount;

    const isExtraChargesEmpty = isEmptyObject(addCharges) && isEmptyObject(discountCharges);

    const isBlockedCase = isInvoiceGenerated && isExtraChargesEmpty;

    dispatch(
      SuccessErrorModalOpen({
        type: 'success',
        title: 'Warning!',
        subTitle: isBlockedCase
          ? 'Cannot lock rates for invoiced shipments without adding any extra charges.'
          : 'Once the rate is locked, it cannot be unlocked. Do you want to continue?',

        btnText: isBlockedCase ? 'OK' : 'Confirm',
        secondaryBtnText: isBlockedCase ? undefined : 'Close',

        onConfirm: isBlockedCase
          ? () => {
              dispatch(SuccessErrorModalReset());
            }
          : () => {
              if (shipmentNumber) {
                dispatch(shipmentLockRate(shipmentNumber));
                dispatch(SuccessErrorModalReset());
              }
            },
      }),
    );
  };

  const handleExtraCharge = (type: 'buying' | 'selling' | 'purchase') => (payload: ExtraChargeType[]) => {
    // Clone the existing extraCharges for the given type

    let shipmentCharge: any;
    if (type === 'purchase') {
      shipmentCharge = purchasePricing ? JSON.parse(JSON.stringify(purchasePricing.extraCharges || {})) : {};
    } else {
      shipmentCharge = extraCharges ? JSON.parse(JSON.stringify(extraCharges[type] || {})) : {};
    }

    // Iterate over each charge in the payload array
    payload.forEach(charge => {
      const chargeObj = {
        name: charge.chargeName,
        value: charge.value,
        applyOn: charge.applyGlobalCharge,
        excludeFromTax: !charge.applyOnGst,
        _courierId: charge._courierId || '',
        _carrierId: charge._carrierId,
      };

      // Merge the charge into the appropriate type (add or discount)
      shipmentCharge[charge.chargeType as 'add' | 'discount'] = {
        ...shipmentCharge[charge.chargeType as 'add' | 'discount'],
        [chargeObj.name]: chargeObj,
      };
    });

    const idToUse = shipmentById._accountId?.isAccountHolder
      ? shipmentById._accountId?._id
      : shipmentById?._userId?._id;
    const isAccountHolder = shipmentById._accountId?.isAccountHolder;

    // Dispatch the update action with the updated shipmentCharge
    if (shipmentNumber) {
      dispatch(
        updateExtraCharge({ shipmentNumber, type, charge: shipmentCharge, accountId: idToUse, isAccountHolder }),
      );
    }
  };

  const onDeleteExtraCharge =
    (type: 'buying' | 'selling' | 'purchase') =>
    ({ chargeName, value }: { chargeName: string; value: number }) => {
      let shipmentCharge: any;
      if (type === 'purchase') {
        shipmentCharge = purchasePricing ? JSON.parse(JSON.stringify(purchasePricing.extraCharges || {})) : {};
      } else {
        shipmentCharge = extraCharges ? JSON.parse(JSON.stringify(extraCharges[type] || {})) : {};
      }
      const chargeType = value > 0 ? 'add' : 'discount';
      delete shipmentCharge[chargeType][chargeName];
      const idToUse = shipmentById._accountId?.isAccountHolder
        ? shipmentById._accountId?._id
        : shipmentById?._userId?._id;
      const isAccountHolder = shipmentById._accountId?.isAccountHolder;
      if (shipmentNumber)
        dispatch(
          updateExtraCharge({ shipmentNumber, type, charge: shipmentCharge, accountId: idToUse, isAccountHolder }),
        );
    };

  const isRatesLock = shipmentById?.isRateLocked || !shipmentActionControl.canUpdateCharges;

  return (
    <div ref={shipmentChargesRef}>
      <Grid container mb={2}>
        <Title title="Shipment Charges" removeBorder removePadding />
      </Grid>
      <Grid
        container
        gap={3}
        style={{
          pointerEvents: shipmentById?.invoicing ? 'none' : 'auto',
          inset: shipmentById?.invoicing ? '0' : '',
          backgroundColor: shipmentById?.invoicing ? 'rgba(0, 0, 0, 0.1)' : 'none',
        }}
        className="p-4 mb-3 border border-solid border-light rounded"
      >
        <AccountDetails carriers={carriers} />

        <Grid container justifyContent={'end'} alignItems={'center'} gap={2} mb={2}>
          {!isRatesLock && (
            <SbButton
              variant="outlined"
              className="rounded-xs"
              sx={{ mr: { xs: 0, md: 1 }, mb: { xs: 1, md: 0 }, height: 36, borderRadius: '4px !important' }}
              onClick={handleLockRate}
              disabled={!isLockRatesWriteAllowed || isRatesLock}
            >
              Lock Rates
            </SbButton>
          )}
          {!isRatesLock && (
            <DateWiseGlobalCharges
              value={selectedDate}
              handleChange={date => setSelectedDate(date)}
              disabled={!isGlobalChargesWriteAllowed || isRatesLock}
            />
          )}
          {purchasePricing && (
            <>
              {' '}
              <Grid container justifyContent="end" mt={5}>
                {' '}
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, cursor: 'pointer' }}
                >
                  {' '}
                  <span>Show Billed Purchase</span>{' '}
                  <Switch
                    checked={showPurchasePricing}
                    onChange={() => setShowPurchasePricing(prev => !prev)}
                    color="primary"
                    size="medium"
                  />{' '}
                </label>{' '}
              </Grid>{' '}
            </>
          )}
        </Grid>

        <Grid container columnSpacing={4}>
          {sellingPricing && isSellingChargesReadAllowed && (
            <Grid item xs={12} md={6}>
              <Grid
                container
                sx={{ p: { xs: 4, md: 8, lg: 8 } }}
                className="mb-3 border border-solid border-light rounded"
              >
                <Charges
                  pricing={sellingPricing?.pricing || []}
                  taxPricing={sellingPricing?.taxPricing || []}
                  totalPricing={sellingPricing?.totalPricing || 0}
                  // currency="INR"
                  currency={sellingPricing?.currency || organizationData?.currency.name || 'INR'}
                  onDeleteExtraCharge={onDeleteExtraCharge('selling')}
                  disable={isRatesLock}
                  label="Sale"
                  writeAccess={isSellingChargesWriteAllowed}
                  carriers={carriers}
                />
                {!isRatesLock && (
                  <ExtraCharges
                    writeAccess={isSellingChargesWriteAllowed}
                    onSave={handleExtraCharge('selling')}
                    AdditionalChargeList={shipmentById?._selectedAdditionalChargeId}
                    calledFor={'selling'}
                    carriers={carriers}
                    mainCarrierId={sellingPricing?._carrierId}
                  />
                )}
              </Grid>
            </Grid>
          )}
          {/* Right Section for Buying or Purchase */}
          {isBuyingChargesReadAllowed && (
            <Grid item xs={12} md={6}>
              <Grid
                container
                sx={{ p: { xs: 4, md: 8, lg: 8 } }}
                className="mb-3 border border-solid border-light rounded"
              >
                {showPurchasePricing && purchasePricing ? (
                  // --- Purchase Pricing Section ---
                  <>
                    <Charges
                      pricing={purchasePricing?.pricing || []}
                      taxPricing={purchasePricing?.taxPricing || []}
                      totalPricing={purchasePricing?.totalPricing || 0}
                      currency={purchasePricing?.currency || organizationData?.currency.name || 'INR'}
                      onDeleteExtraCharge={onDeleteExtraCharge('purchase')}
                      disable={isRatesLock}
                      label="Purchase - Billed"
                      writeAccess={isBuyingChargesWriteAllowed}
                      serviceName={purchasePricing?.serviceName}
                      carriers={carriers}
                    />
                    {/* ExtraCharges shown only when purchasePricing exists and toggle ON */}

                    {/* {!isRatesLock && ( */}
                    <ExtraCharges
                      writeAccess={isBuyingChargesWriteAllowed}
                      onSave={handleExtraCharge('purchase')}
                      AdditionalChargeList={shipmentById?._selectedAdditionalChargeMaskedId}
                      calledFor={'purchase'}
                      carriers={carriers}
                      pricing={purchasePricing?.pricing || []}
                      mainCarrierId={purchasePricing?._carrierId}
                    />
                    {/* // )} */}
                    {purchasePricing?.chargeableWeight && (
                      <Grid
                        sx={{
                          borderRadius: '4px',
                          padding: '0 5px',
                          marginTop: '20px',
                        }}
                        container
                        justifyContent={'space-between'}
                      >
                        <Typography className="md-text font-semibold">Chargeable Weight</Typography>
                        <Typography className="md-text font-semibold">
                          {purchasePricing?.chargeableWeight} kg
                        </Typography>
                      </Grid>
                    )}
                  </>
                ) : (
                  // --- Buying Pricing Section ---
                  <>
                    <Charges
                      pricing={buyingPricing?.pricing || []}
                      taxPricing={buyingPricing?.taxPricing || []}
                      totalPricing={buyingPricing?.totalPricing || 0}
                      currency={buyingPricing?.currency || organizationData?.currency.name || 'INR'}
                      onDeleteExtraCharge={onDeleteExtraCharge('buying')}
                      // disable={isRatesLock}
                      label="Purchase"
                      writeAccess={isBuyingChargesWriteAllowed}
                      carriers={carriers}
                    />

                    {/* Show ExtraCharges only when purchasePricing is NOT present */}
                    {!purchasePricing && (
                      <ExtraCharges
                        writeAccess={isBuyingChargesWriteAllowed}
                        onSave={handleExtraCharge('buying')}
                        AdditionalChargeList={shipmentById?._selectedAdditionalChargeMaskedId}
                        calledFor={'buying'}
                        carriers={carriers}
                        mainCarrierId={buyingPricing?._carrierId}
                      />
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
}
